/**
 * What a feedback message is sent with, beside the page: the few facts about
 * the browser that explain most "this looks wrong" reports -- which browser,
 * how wide the window, how zoomed, and which release.
 *
 * Deliberately short. Nothing that identifies the device or where it is: the
 * message is signed already, and this is for reading the layout, not the
 * person. The dialog says what is sent.
 */
export type ClientContext = {
  browser: string;
  window: string;
  pixelRatio: number;
  language: string;
  appVersion: string;
};

// Set by build.ts from package.json. In local development nothing replaces
// it, and the browser has no `process` to read it from, so it is caught
// rather than guarded with `typeof process` -- which would also be false in a
// production build, where the bundler has already put the version in.
function appVersion(): string | undefined {
  try {
    return process.env.PUBLIC_APP_VERSION as string | undefined;
  } catch {
    return undefined;
  }
}

/** "Firefox 144 on Windows", from the user-agent string. */
function describeBrowser(userAgent: string): string {
  const version = (pattern: RegExp) => pattern.exec(userAgent)?.[1];
  const edge = version(/Edg\/(\d+)/);
  const firefox = version(/Firefox\/(\d+)/);
  const chrome = version(/Chrome\/(\d+)/);
  const safari = /Safari\//.test(userAgent) ? version(/Version\/(\d+)/) : undefined;
  const browser = edge ? `Edge ${edge}`
    : firefox ? `Firefox ${firefox}`
      : chrome ? `Chrome ${chrome}`
        : safari ? `Safari ${safari}`
          : "An unknown browser";
  const system = /Windows/.test(userAgent) ? "Windows"
    : /iPhone|iPad/.test(userAgent) ? "iOS"
      : /Mac OS X/.test(userAgent) ? "macOS"
        : /Android/.test(userAgent) ? "Android"
          : /Linux/.test(userAgent) ? "Linux"
            : "an unknown system";
  return `${browser} on ${system}`;
}

export function clientContext(): ClientContext {
  return {
    browser: describeBrowser(navigator.userAgent),
    window: `${window.innerWidth} × ${window.innerHeight}`,
    pixelRatio: Math.round(window.devicePixelRatio * 100) / 100,
    language: navigator.language,
    appVersion: appVersion() ?? "development",
  };
}
